/**
 * Эмуляция плохого интернета для отладки оверлея (IMGM-1435).
 *
 * Подменяет `fetch`, `XMLHttpRequest` и `WebSocket`, чтобы воспроизвести условия стадиона:
 * долгие ответы, потерянные запросы и периодические обрывы связи.
 *
 * IMPORTANT: перехватчики ставятся **при импорте модуля**, а не при нажатии кнопки, и импорт идёт
 * первой строкой `src/index.tsx`. Иначе эмуляция не ловит почти ничего:
 * - `@supabase/supabase-js` запоминает `fetch` один раз, в конструкторе клиента
 *   (`lib/fetch.js`, `_fetch = fetch`), а клиент создаётся на импорте `modules/core/supabase/supabase`;
 * - `axios@0.25` в браузере ходит через `XMLHttpRequest`, а не через `fetch`, поэтому GraphQL-запросы
 *   патч `fetch` не видит вовсе.
 * Пока режим выключен, обёртки просто зовут оригинал.
 *
 * Запросы на localhost не трогаются — иначе дев-сервер и HMR перезагружали бы страницу.
 */

export interface PoorNetworkSettings {
  /** Минимальная задержка ответа, мс. */
  minLatencyMs: number;
  /** Максимальная задержка ответа, мс. */
  maxLatencyMs: number;
  /** Доля запросов, которые вообще не доедут (0..1). */
  lossRate: number;
}

export interface FlappingSettings {
  /** Сколько держим связь оборванной, мс. */
  downMs: number;
  /** Сколько держим связь живой, мс. */
  upMs: number;
}

export const DEFAULT_POOR_NETWORK: PoorNetworkSettings = {
  minLatencyMs: 1_500,
  maxLatencyMs: 4_000,
  lossRate: 0.3,
};

export const DEFAULT_FLAPPING: FlappingSettings = {
  downMs: 10_000,
  upMs: 20_000,
};

type Listener = () => void;

/** Адрес, на который заворачиваются вебсокеты во время «обрыва»: там никто не слушает. */
const BLACKHOLE_WS_URL = 'ws://127.0.0.1:9/network-chaos';

function isLocalUrl(url: string): boolean {
  // `data:` и `blob:` сети не касаются — тормозить их значило бы врать про причину.
  if (url.startsWith('data:') || url.startsWith('blob:')) return true;
  return url.includes('localhost') || url.includes('127.0.0.1');
}

function toUrlString(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

class NetworkChaos {
  private isInstalled = false;

  private poorSettings: PoorNetworkSettings | null = null;
  private flappingSettings: FlappingSettings | null = null;
  private flappingTimer?: ReturnType<typeof setTimeout>;

  private offline = false;
  private readonly sockets = new Set<WebSocket>();
  private readonly listeners = new Set<Listener>();

  get isPoorNetwork(): boolean {
    return this.poorSettings !== null;
  }

  get isFlapping(): boolean {
    return this.flappingSettings !== null;
  }

  get isOffline(): boolean {
    return this.offline;
  }

  /** Подписка на изменение состояния — чтобы кнопки перерисовывались. */
  public subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  public enablePoorNetwork(settings: PoorNetworkSettings = DEFAULT_POOR_NETWORK) {
    this.poorSettings = settings;
    console.warn({ event: 'network chaos: poor network on', settings });
    this.notify();
  }

  public disablePoorNetwork() {
    this.poorSettings = null;
    console.warn({ event: 'network chaos: poor network off' });
    this.notify();
  }

  public startFlapping(settings: FlappingSettings = DEFAULT_FLAPPING) {
    this.flappingSettings = settings;
    console.warn({ event: 'network chaos: flapping on', settings });
    this.notify();
    this.goOffline();
  }

  public stopFlapping() {
    this.flappingSettings = null;
    if (this.flappingTimer) clearTimeout(this.flappingTimer);
    this.flappingTimer = undefined;
    console.warn({ event: 'network chaos: flapping off' });
    if (this.offline) this.goOnline();
    else this.notify();
  }

  public stopAll() {
    this.stopFlapping();
    this.disablePoorNetwork();
  }

  /**
   * Ставит перехватчики. Зовётся на импорте модуля — до создания supabase-клиента,
   * который запоминает `fetch` навсегда.
   */
  public install() {
    if (this.isInstalled) return;
    if (typeof window === 'undefined') return;
    this.isInstalled = true;

    this.installFetch();
    this.installXhr();
    this.installImage();
    this.installWebSocket();
  }

  private goOffline() {
    this.offline = true;
    console.warn({ event: 'network chaos: offline' });
    this.dropSockets();
    window.dispatchEvent(new Event('offline'));
    this.notify();

    if (!this.flappingSettings) return;
    this.flappingTimer = setTimeout(() => this.goOnline(), this.flappingSettings.downMs);
  }

  private goOnline() {
    this.offline = false;
    console.warn({ event: 'network chaos: online' });
    window.dispatchEvent(new Event('online'));
    this.notify();

    if (!this.flappingSettings) return;
    this.flappingTimer = setTimeout(() => this.goOffline(), this.flappingSettings.upMs);
  }

  /** Рвём живые вебсокеты кодом из пользовательского диапазона — realtime-клиент пойдёт переподключаться. */
  private dropSockets() {
    this.sockets.forEach((socket) => {
      try {
        socket.close(4000, 'network-chaos');
      } catch (error) {
        console.debug({ event: 'network chaos: socket close failed', error });
      }
    });
    this.sockets.clear();
  }

  private notify() {
    this.listeners.forEach((listener) => listener());
  }

  /**
   * Что сделать с запросом: пропустить (`null`), задержать (число мс) или уронить (`'drop'`).
   */
  private plan(url: string): number | 'drop' | null {
    if (isLocalUrl(url)) return null;
    if (this.offline) return 'drop';

    const settings = this.poorSettings;
    if (!settings) return null;
    if (Math.random() < settings.lossRate) return 'drop';

    return settings.minLatencyMs + Math.random() * (settings.maxLatencyMs - settings.minLatencyMs);
  }

  private installFetch() {
    const originalFetch = window.fetch.bind(window);
    const chaos = this;

    window.fetch = async function chaosFetch(input: RequestInfo | URL, init?: RequestInit) {
      const url = toUrlString(input);
      const plan = chaos.plan(url);

      if (plan === 'drop') {
        await delay(chaos.isOffline ? 200 : (chaos.poorSettings?.minLatencyMs ?? 0));
        console.warn({ event: 'network chaos: fetch dropped', url });
        throw new TypeError('Failed to fetch');
      }

      if (plan !== null) await delay(plan);

      return originalFetch(input, init);
    };
  }

  /** axios@0.25 в браузере ходит через XHR — без этого патча GraphQL-запросы эмуляцию не замечают. */
  private installXhr() {
    const OriginalXhr = window.XMLHttpRequest;
    const chaos = this;

    class ChaosXhr extends OriginalXhr {
      private chaosUrl = '';

      open(method: string, url: string | URL, async?: boolean, username?: string | null, password?: string | null) {
        this.chaosUrl = typeof url === 'string' ? url : url.toString();
        super.open(method, url, async ?? true, username, password);
      }

      send(body?: Document | XMLHttpRequestBodyInit | null) {
        const plan = chaos.plan(this.chaosUrl);

        if (plan === 'drop') {
          console.warn({ event: 'network chaos: xhr dropped', url: this.chaosUrl });
          setTimeout(() => this.dispatchEvent(new ProgressEvent('error')), 200);
          return;
        }

        if (plan === null) {
          super.send(body);
          return;
        }

        setTimeout(() => super.send(body), plan);
      }
    }

    window.XMLHttpRequest = ChaosXhr as unknown as typeof XMLHttpRequest;
  }

  /**
   * Картинки табло Fabric грузит присваиванием `img.src` (`fabric.js:1090-1131`), а не через
   * `fetch` или XHR. Без этого патча эмуляция не задевает ровно ту асинхронность, на которой
   * накладываются две загрузки табло: `loadLocal` ждёт `enlivenObjects`, а тот — картинки.
   */
  private installImage() {
    const descriptor = Object.getOwnPropertyDescriptor(HTMLImageElement.prototype, 'src');
    if (!descriptor?.get || !descriptor.set) return;

    const { get: originalGet, set: originalSet } = descriptor;
    const chaos = this;

    Object.defineProperty(HTMLImageElement.prototype, 'src', {
      configurable: true,
      enumerable: descriptor.enumerable,
      get(this: HTMLImageElement) {
        return originalGet.call(this);
      },
      set(this: HTMLImageElement, value: string) {
        const plan = chaos.plan(String(value));

        if (plan === 'drop') {
          console.warn({ event: 'network chaos: image dropped', url: value });
          setTimeout(() => this.dispatchEvent(new Event('error')), 200);
          return;
        }

        if (plan === null) {
          originalSet.call(this, value);
          return;
        }

        setTimeout(() => originalSet.call(this, value), plan);
      },
    });
  }

  private installWebSocket() {
    const OriginalWebSocket = window.WebSocket;
    const chaos = this;

    class ChaosWebSocket extends OriginalWebSocket {
      constructor(url: string | URL, protocols?: string | string[]) {
        const address = typeof url === 'string' ? url : url.toString();
        const isLocal = isLocalUrl(address);
        const target = chaos.isOffline && !isLocal ? BLACKHOLE_WS_URL : address;

        super(target, protocols);

        if (isLocal) return;

        chaos.sockets.add(this);
        this.addEventListener('close', () => chaos.sockets.delete(this));
      }
    }

    window.WebSocket = ChaosWebSocket as unknown as typeof WebSocket;
  }
}

export const networkChaos = new NetworkChaos();
